// The "Drafts" queue in Shift Validation.
//
// Why this screen exists. validate_shift_v2 finished with
//   update shifts set status = case when v_flag then 'flagged' else 'validated' end
// and v_flag is raised only by a check that CONTRADICTS the shift. A check that
// finds no evidence returns 'pending', which contradicts nothing. So a shift with
// no Uber evidence at all, a mis-dated one included, was written to 'validated'
// and entered pay. 24 of 488 August shifts were in exactly that state.
//
// The distinction this screen is built around: 21 of those 24 fall on 8 August,
// and 8 August captured 3 Uber evidence rows against 21 shifts. Those men worked.
// Fleetly cannot see the day. A nearest-neighbour rule would have moved all 21
// onto 7 or 9 August, which is why the coverage ratio is on the screen and why a
// report gap is shown in a different colour from a wrong date. One is Fleetly's
// problem and one is a form to correct.
//
// Nothing is computed here. Every figure, every verdict and every sentence comes
// from v_shift_draft_queue and v_shift_date_mismatch.
import { useMemo, useState } from 'react'
import { CalendarX2, FileWarning, CheckCircle2, ArrowRight, Wand2 } from 'lucide-react'
import { Chip, type ChipTone } from '../interviews/Chip'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../lib/shadcn/table'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { formatZar, formatDate } from '../../utils/interviews'

export type ShiftDraftRow = {
  shift_id: string
  shift_date: string
  shift_window: string | null
  status: string | null
  driver_name: string | null
  plate: string | null
  earnings_at_risk: number | string | null
  hold_reason: string | null
  days_since_shift: number | string | null
  ageing: boolean | null
  date_captured: boolean | null
  coverage_ratio: number | string | null
  fleet_evidence_rows_that_day: number | string | null
  date_note: string | null
  cartrack_trips: number | string | null
}

export type ShiftDateMismatchRow = {
  shift_id: string
  driver_name: string | null
  plate: string | null
  earnings: number | string | null
  declared_date: string
  declared_window: string | null
  proposed_date: string | null
  proposed_trips: number | string | null
  proposed_plate: string | null
  coverage_ratio: number | string | null
  autofixable: boolean | null
  detail: string | null
}

const n = (v: number | string | null | undefined): number => {
  const parsed = v == null || v === '' ? 0 : Number(v)
  return Number.isFinite(parsed) ? parsed : 0
}

// The hold reason arrives as a sentence from the database. These are the codes
// inside it, mapped to a tone and a heading. A report gap is amber because it is
// Fleetly's failure; a suspected wrong date is red because it needs a decision.
function holdKind(reason: string | null | undefined): {
  code: string
  label: string
  tone: ChipTone
} {
  const r = reason ?? ''
  if (r.includes('report_gap'))
    return { code: 'report_gap', label: 'Report never arrived', tone: 'amber' }
  if (r.includes('wrong_date_suspected'))
    return { code: 'wrong_date', label: 'Date looks wrong', tone: 'red' }
  if (r.includes('driver_not_linked'))
    return { code: 'not_linked', label: 'Driver not linked to Uber', tone: 'amber' }
  if (r.includes('no_corroboration'))
    return { code: 'no_corroboration', label: 'Nothing corroborates it', tone: 'red' }
  return { code: 'other', label: 'Held', tone: 'neutral' }
}

export function ShiftDraftQueue({
  drafts,
  dateMismatches,
  canAct,
  gateOn,
  onRelease,
  onFixDates,
  busy,
}: {
  drafts: ShiftDraftRow[]
  dateMismatches: ShiftDateMismatchRow[]
  canAct: boolean
  gateOn: boolean
  onRelease: (shiftId: string, reason: string) => void
  onFixDates: () => void
  busy: boolean
}) {
  const [releasing, setReleasing] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const moneyHeld = useMemo(
    () => drafts.reduce((sum, d) => sum + n(d.earnings_at_risk), 0),
    [drafts],
  )
  const autofixable = useMemo(
    () => dateMismatches.filter((m) => m.autofixable === true).length,
    [dateMismatches],
  )
  const byDate = useMemo(() => {
    const map = new Map<string, ShiftDraftRow[]>()
    for (const d of drafts) {
      const list = map.get(d.shift_date)
      if (list) list.push(d)
      else map.set(d.shift_date, [d])
    }
    return Array.from(map.entries())
  }, [drafts])

  return (
    <div className="space-y-5">
      {/* What state the gate is actually in. Never leave an operator guessing
          whether the queue is advisory or is holding somebody's wages. */}
      <div
        className={cn(
          'flex items-start gap-2 rounded-md border p-3 text-sm',
          gateOn
            ? 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100'
            : 'border-border bg-muted/50 text-muted-foreground',
        )}
      >
        <FileWarning className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">
            {gateOn
              ? 'The draft gate is ON. These shifts are being held out of pay.'
              : 'The draft gate is OFF. This is what it would hold, nothing is being held yet.'}
          </p>
          <p className="text-xs leading-relaxed">
            A shift is a draft when neither Uber nor the tracker corroborates it. Releasing one
            pays it on a person&rsquo;s authority, so the reason is kept on the shift. With the gate
            off these rows are a preview: the reasons are recorded, the statuses are untouched.
            {gateOn && drafts.length > 0 ? (
              <>
                {' '}
                <span className="font-medium">
                  {formatZar(moneyHeld)} across {drafts.length} shift
                  {drafts.length === 1 ? '' : 's'} is waiting on this queue.
                </span>
              </>
            ) : null}
          </p>
        </div>
      </div>

      {/* Suspected wrong dates first: few, actionable, and the thing that moves. */}
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <CalendarX2 className="h-4 w-4" />
            Dates Uber contradicts
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
              {dateMismatches.length}
            </span>
          </h2>
          {canAct && autofixable > 0 ? (
            <button
              type="button"
              disabled={busy}
              onClick={onFixDates}
              className="flex items-center gap-1.5 rounded-md border border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--brand-soft-foreground))] transition-colors hover:opacity-90 disabled:opacity-50"
            >
              {busy ? <Spinner className="h-3 w-3" /> : <Wand2 className="h-3 w-3" />}
              Move the {autofixable} clear-cut {autofixable === 1 ? 'one' : 'ones'}
            </button>
          ) : null}
        </div>

        {dateMismatches.length === 0 ? (
          <p className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            No shift has a declared date that Uber contradicts on a date the fleet can see.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Declared</TableHead>
                  <TableHead />
                  <TableHead>Uber&rsquo;s evidence</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Earnings</TableHead>
                  <TableHead>Clear-cut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dateMismatches.map((m) => (
                  <TableRow key={m.shift_id}>
                    <TableCell className="font-medium whitespace-nowrap">{m.driver_name ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(m.declared_date)}
                      <span className="ml-1 text-xs text-muted-foreground">{m.declared_window}</span>
                    </TableCell>
                    <TableCell>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(m.proposed_date)}
                      <span className="ml-1 text-xs text-muted-foreground">
                        {n(m.proposed_trips)} trips
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      {m.proposed_plate === m.plate ? (
                        <Chip tone="green">{m.plate ?? '—'} matches</Chip>
                      ) : (
                        <Chip tone="amber">
                          {m.plate ?? 'none'} vs {m.proposed_plate ?? 'none'}
                        </Chip>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {formatZar(m.earnings)}
                    </TableCell>
                    <TableCell>
                      {m.autofixable ? (
                        <Chip tone="green">Yes</Chip>
                      ) : (
                        <Chip tone="amber">Needs a person</Chip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* The holding pen, grouped by date, because a report gap is a whole-day event. */}
      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          Held as drafts
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
            {drafts.length}
          </span>
        </h2>

        {drafts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <CheckCircle2 className="h-8 w-8 opacity-40" />
            Nothing is held. Every shift is corroborated by Uber or by the tracker.
          </div>
        ) : (
          <div className="space-y-4">
            {byDate.map(([date, rows]) => {
              const first = rows[0]!
              const kind = holdKind(first.hold_reason)
              const gap = kind.code === 'report_gap'
              return (
                <div key={date} className="overflow-hidden rounded-lg border border-border">
                  <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
                    <span className="text-sm font-medium text-foreground">{formatDate(date)}</span>
                    <Chip tone={kind.tone}>{kind.label}</Chip>
                    <span className="text-xs text-muted-foreground">
                      {rows.length} shift{rows.length === 1 ? '' : 's'} ·{' '}
                      {formatZar(rows.reduce((sum, r) => sum + n(r.earnings_at_risk), 0))} waiting
                    </span>
                    {gap ? (
                      <span className="text-xs text-muted-foreground">
                        · Uber sent {n(first.fleet_evidence_rows_that_day)} rows for the whole fleet,
                        coverage {n(first.coverage_ratio).toFixed(2)}
                      </span>
                    ) : null}
                  </div>

                  {gap ? (
                    <p className="border-b border-border bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:bg-amber-950 dark:text-amber-100">
                      Fleetly cannot see this date. These drivers almost certainly worked, and their
                      dates are almost certainly right. Nothing here is a finding about any of them.
                      What is needed is the Uber report for this date, or a release on the strength
                      of the tracker and the driver&rsquo;s word.
                    </p>
                  ) : null}

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Driver</TableHead>
                          <TableHead>Window</TableHead>
                          <TableHead>Vehicle</TableHead>
                          <TableHead className="text-center">Tracker trips</TableHead>
                          <TableHead>Earnings waiting</TableHead>
                          <TableHead className="text-center">Days</TableHead>
                          {canAct ? <TableHead>Release</TableHead> : null}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.shift_id}>
                            <TableCell className="font-medium whitespace-nowrap">
                              {r.driver_name ?? '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-xs">
                              {r.shift_window ?? '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-xs">
                              {r.plate ?? '—'}
                            </TableCell>
                            <TableCell className="text-center tabular-nums">
                              {r.cartrack_trips == null ? (
                                <span className="text-muted-foreground">—</span>
                              ) : n(r.cartrack_trips) > 0 ? (
                                <Chip tone="green">{n(r.cartrack_trips)}</Chip>
                              ) : (
                                <Chip tone="neutral">none</Chip>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap tabular-nums">
                              {formatZar(r.earnings_at_risk)}
                            </TableCell>
                            <TableCell className="text-center tabular-nums">
                              {r.ageing ? (
                                <Chip tone="red">{n(r.days_since_shift)}</Chip>
                              ) : (
                                n(r.days_since_shift)
                              )}
                            </TableCell>
                            {canAct ? (
                              <TableCell>
                                {releasing === r.shift_id ? (
                                  <div className="flex flex-wrap items-center gap-1">
                                    <input
                                      autoFocus
                                      value={reason}
                                      onChange={(e) => setReason(e.target.value)}
                                      placeholder="What did you check?"
                                      className="w-48 rounded border border-border bg-background px-2 py-1 text-xs"
                                    />
                                    <button
                                      type="button"
                                      disabled={busy || reason.trim() === ''}
                                      onClick={() => {
                                        onRelease(r.shift_id, reason.trim())
                                        setReleasing(null)
                                        setReason('')
                                      }}
                                      className="rounded border border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] px-2 py-1 text-xs font-medium text-[hsl(var(--brand-soft-foreground))] disabled:opacity-40"
                                    >
                                      Release
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setReleasing(null)
                                        setReason('')
                                      }}
                                      className="rounded border border-border px-2 py-1 text-xs text-muted-foreground"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReleasing(r.shift_id)
                                      setReason('')
                                    }}
                                    className="rounded border border-border px-2 py-1 text-xs font-medium text-foreground hover:bg-muted"
                                  >
                                    Release into pay
                                  </button>
                                )}
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
